// Walidacje danych osoby zewnętrznej — lustrzane wobec backendu (Helpers/PortalValidation.cs),
// żeby użytkownik dostawał błędy natychmiast, a nie dopiero po wysłaniu.

// PESEL usunięty z całej aplikacji decyzją klienta 2026-08-22 — tożsamość potwierdzają
// typ i numer dokumentu oraz dane urodzenia (data DD-MM-RRRR, miejscowość, kraj).

/**
 * Typy dokumentu tożsamości — lustro enuma IdentityDocumentType z backendu.
 * Wybór jest OBOWIĄZKOWY wszędzie, gdzie podaje się numer: od typu zależy reguła walidacji
 * (wymóg klienta 2026-08-09).
 */
export const DOCUMENT_TYPES = [
    { value: 1, label: "Dowód osobisty", placeholder: "ABC123456" },
    { value: 2, label: "Paszport", placeholder: "np. EA1234567" },
];

export const documentTypeLabel = (value) =>
    DOCUMENT_TYPES.find((t) => t.value === Number(value))?.label || "Dokument tożsamości";

/** Numer dokumentu do postaci zapisywanej: bez białych znaków, wersalikami. */
export const normalizeDocumentNumber = (value) => (value || "").replace(/\s/g, "").toUpperCase();

export const isValidDocumentNumber = (value, type) => {
    const doc = normalizeDocumentNumber(value);
    // PASZPORT: tylko reguła miękka. Suma kontrolna polskiego dowodu nie ma tu zastosowania —
    // paszporty zagraniczne mają dziesiątki formatów i nie wolno ich odrzucać.
    if (Number(type) === 2) {
        return /^[A-Z0-9]{5,30}$/.test(doc);
    }
    if (/^[A-Z]{3}\d{6}$/.test(doc)) {
        // Polski dowód: wagi 7,3,1 z pominięciem cyfry kontrolnej (pozycja 4).
        const weights = [7, 3, 1, 0, 7, 3, 1, 7, 3];
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            if (i === 3) continue;
            const charValue = /\d/.test(doc[i]) ? Number(doc[i]) : doc.charCodeAt(i) - 65 + 10;
            sum += charValue * weights[i];
        }
        return sum % 10 === Number(doc[3]);
    }
    // Inne dokumenty (paszport, karta pobytu) — walidacja miękka.
    return /^[A-Z0-9]{5,30}$/.test(doc);
};

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email || "");

export const normalizePhone = (phone) => {
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length === 9) return `48${digits}`;
    return digits.length >= 11 && digits.length <= 15 ? digits : null;
};

// Backend zwraca daty kalendarzowe jako "yyyy-MM-dd" — formatujemy je tekstowo,
// bo new Date("2026-08-05") to północ UTC i w innej strefie wyszedłby dzień wcześniej.
export const formatDatePl = (value) => {
    if (!value) return "—";
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (match) {
        const [, year, month, day] = match;
        return `${Number(day)}.${Number(month)}.${year}`;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pl-PL");
};

/**
 * Data urodzenia: realna — nie w przyszłości i nie starsza niż 120 lat.
 * Wejście w ISO ("yyyy-MM-dd"), bo tym formatem posługuje się DatePicker aplikacji; do widoku
 * idzie DD-MM-RRRR (patrz formatBirthDatePl).
 */
export const isValidBirthDate = (iso) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return false;
    const today = localTodayIso();
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    // Odrzuca daty niemożliwe kalendarzowo (31-02 przesuwa się na marzec).
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return false;
    const najstarsza = `${y + 120}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return iso <= today && najstarsza >= today;
};

/**
 * Najwcześniejsza akceptowana data urodzenia — dokładnie ta sama granica, którą sprawdza
 * `isValidBirthDate` (120 lat wstecz) i jej serwerowe lustro `PortalValidation.IsValidBirthDate`.
 *
 * Po co osobna funkcja: kalendarz dostaje ją jako `min`, więc szybkie przewijanie po latach
 * kończy się tam, gdzie kończy się walidacja. Bez tego picker pokazywałby rok 1830, a formularz
 * odpowiadał „data urodzenia jest nierealna" — użytkownik dostawałby błąd za wybór, który
 * aplikacja sama mu podsunęła.
 */
export const birthDateFloorIso = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear() - 120}-${month}-${day}`;
};

/** Data urodzenia w formacie wymaganym przez klienta: DD-MM-RRRR. */
export const formatBirthDatePl = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
    return match ? `${match[3]}-${match[2]}-${match[1]}` : "—";
};

/** Łączniki w nazwach geograficznych pisane małą literą, gdy nie stoją na początku. */
const LACZNIKI = new Set([
    "nad", "pod", "przy", "na", "u", "w", "we", "am", "an", "der", "des", "de", "del",
    "di", "da", "do", "du", "la", "le", "les", "van", "von", "y", "of", "the", "upon",
]);

/**
 * Miejscowość albo kraj od wielkiej litery (wymóg klienta). Zbiór znaków jest szerszy niż
 * przy imieniu — apostrof i kropka są w prawdziwych nazwach (L'Aquila, St. Petersburg).
 * Zwraca null dla wartości z cyframi albo innymi znakami: to błąd danych, nie literówka
 * do cichego poprawienia.
 */
export const normalizePlaceName = (raw) => {
    const value = String(raw || "").trim().replace(/\s+/g, " ");
    if (!value) return null;
    if (!/^[\p{L} \-'.]+$/u.test(value)) return null;
    const czlony = value.split(" ").map((czlon, index) => {
        const maly = czlon.toLocaleLowerCase("pl-PL");
        if (index > 0 && LACZNIKI.has(maly)) return maly;
        return maly.replace(/(^|[-'.])(\p{L})/gu, (_, sep, litera) => sep + litera.toLocaleUpperCase("pl-PL"));
    });
    const wynik = czlony.join(" ").replace(/^[-\s]+|[-\s]+$/g, "");
    return wynik.length >= 2 ? wynik : null;
};

export const isValidPlaceName = (raw) => normalizePlaceName(raw) !== null;

/**
 * JEDNO miejsce, w którym front rozstrzyga poprawność danych tożsamości — lustro
 * PortalValidation.ValidateIdentity z backendu. Zwraca { field, message } albo null.
 *
 * requireIdentity = true na ścieżce WNIOSKU (dokument i dane urodzenia obowiązkowe),
 * false w kartotece (pola opcjonalne, ale wypełnione muszą być poprawne).
 */
export const validateIdentity = (dane, requireIdentity) => {
    const { documentType, documentNumber, birthDate, birthPlace, birthCountry } = dane;
    const dokument = normalizeDocumentNumber(documentNumber);

    if (!dokument) {
        if (requireIdentity) return { field: "documentNumber", message: "Podaj numer dokumentu tożsamości." };
    } else {
        if (!Number(documentType)) {
            return { field: "documentType", message: "Wybierz typ dokumentu — dowód osobisty albo paszport." };
        }
        if (!isValidDocumentNumber(dokument, documentType)) {
            return {
                field: "documentNumber",
                message: Number(documentType) === 2
                    ? "Numer paszportu jest niepoprawny — podaj 5-30 znaków (litery i cyfry, bez spacji)."
                    : "Numer dowodu osobistego jest niepoprawny (błędna suma kontrolna).",
            };
        }
    }

    // PESEL usunięty decyzją klienta 2026-08-22 — dane urodzenia są jedyną ścieżką
    // potwierdzenia tożsamości, dlatego przy requireIdentity są obowiązkowe zawsze.
    if (requireIdentity || birthDate) {
        if (!isValidBirthDate(birthDate)) {
            return {
                field: "birthDate",
                message: birthDate
                    ? "Data urodzenia jest nierealna — sprawdź dzień, miesiąc i rok."
                    : "Podaj datę urodzenia.",
            };
        }
    }
    if (requireIdentity || birthPlace) {
        if (!isValidPlaceName(birthPlace)) {
            return {
                field: "birthPlace",
                message: birthPlace
                    ? "Miejscowość urodzenia może zawierać wyłącznie litery, spacje, myślnik, apostrof i kropkę."
                    : "Podaj miejscowość urodzenia.",
            };
        }
    }
    if (requireIdentity || birthCountry) {
        if (!isValidPlaceName(birthCountry)) {
            return {
                field: "birthCountry",
                message: birthCountry
                    ? "Kraj urodzenia może zawierać wyłącznie litery, spacje, myślnik, apostrof i kropkę."
                    : "Podaj kraj urodzenia.",
            };
        }
    }
    return null;
};

/// Dzisiejsza data w czasie LOKALNYM (toISOString dałby datę UTC — po północy
/// przepuszczałby wczorajszy termin, który backend potem odrzuca).
export const localTodayIso = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
};
